import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { InvitationService } from '../../services';
import { handleNucleusResponse } from '../../services/utils';
import type {
  CreateInvitationRequest,
  InvitationFilters,
  UpdateInvitationRequest,
} from '@billx/types';
import { getTokenManager } from '../../config/tokenManager';
import {
  deleteQueryItem,
  sanitizeFilters,
  updateQueryItem,
  upsertQueryItem,
} from '@billx/utils';

export const useUserInvitations = (
  queryFilters: InvitationFilters,
  options: {
    enabled?: boolean;
  } = {
    enabled: true,
  }
) => {
  const filters = sanitizeFilters(queryFilters);
  const queryClient = useQueryClient();

  const QUERY_KEY = ['userInvitations', filters];
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await InvitationService.fetchInvitationsForUser({
        status: filters.status,
      });
      return handleNucleusResponse(response).data;
    },
    enabled: options.enabled,
  });

  const createInvitation = useMutation({
    mutationFn: (data: CreateInvitationRequest) =>
      InvitationService.createInvitation(data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, upsertQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateInvitation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvitationRequest }) =>
      InvitationService.updateInvitation(id, data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, updateQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const deleteInvitation = useMutation({
    mutationFn: (id: string) => InvitationService.deleteInvitation(id),
    onSuccess: (response, id) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, deleteQueryItem(id));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const acceptInvitation = useMutation({
    mutationFn: (id: string) => InvitationService.acceptInvitation(id),
    onSuccess: (response, id) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, deleteQueryItem(id));
        getTokenManager().showNotification({
          message: 'Invitation accepted successfully',
          severity: 'success',
        });
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const declineInvitation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      InvitationService.declineInvitation(id, userId),
    onSuccess: (response, { id }) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, deleteQueryItem(id));
        getTokenManager().showNotification({
          message: 'Invitation declined',
          severity: 'info',
        });
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const { data: invitations = [], ...restQuery } = query;
  return {
    // Query data
    invitations,
    ...restQuery,
    // Mutations
    createInvitation,
    updateInvitation,
    deleteInvitation,
    acceptInvitation,
    declineInvitation,
  };
};
